import React, { useContext, useState, useMemo, useCallback, memo, useRef, useEffect } from 'react';
import { Card, Table, Form, Button } from 'react-bootstrap';
import { ChevronDown, ChevronRight, SortDown, SortUp, Pencil } from 'react-bootstrap-icons';
import { TradingContext } from '../contexts/TradingContext';
import { AppStateContext } from '../contexts/AppStateContext';
import { AuthContext } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useOrdersManager } from '../hooks/useOrdersManager';
import { BannerContext } from '../contexts/BannerContext';
import { useCancelOrder, useUpdateOrder } from '../hooks/useTradeStation';
import useIsMobile from '../hooks/useIsMobile';
import OrderEditModal from './StockChart/OrderEditModal';
import LoadingSpinner from './shared/LoadingSpinner';
import { useLocation, useNavigate } from 'react-router-dom';
import { getOrderFillTimeMs } from '../services/tradestationUtils';
import { parseCancelOrderFailure } from '../utils/orderResultUtils';
import { canAccessTradeWorkspace } from '../utils/brokerConnection';
import { isDashboardPath, navigateToTradeWithSymbol } from '../utils/tradeNavigation';

const ORDERS_PAGE_SIZE = 50;

function ColResizeHandle({ columnKey, onResizeStart }) {
  return (
    <span
      className="orders-col-resize-handle"
      onMouseDown={(e) => onResizeStart(columnKey, e)}
      onClick={(e) => e.stopPropagation()}
      aria-hidden="true"
    />
  );
}

// OPTIMIZED: Cache currency formatter at module level
const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
});

// Memoized row component to prevent unnecessary re-renders
const OrdersTableRow = memo(({ 
  order, 
  colors, 
  formatCurrency, 
  formatDateTime, 
  getOrderStatusBadge, 
  isCancelable, 
  handleEditOrder, 
  activeChartId, 
  updateChartSettings, 
  isMobile,
  canSelectTicker = true,
  onDashboard = false,
  onNavigateToTrade = null,
}) => {
  const leg = order.Legs?.[0] || {};
  
  const handleSymbolClick = useCallback(() => {
    if (!canSelectTicker || !leg.Symbol) return;

    // Portfolio / dashboard: open Trade with chart symbol (same as PositionsTable).
    if (onDashboard) {
      onNavigateToTrade?.(leg.Symbol);
      return;
    }

    if (activeChartId) {
      updateChartSettings(activeChartId, { ticker: leg.Symbol });
    } else {
      console.warn('⚠️ [OrdersTable] No activeChartId available');
    }
    
    if (isMobile) {
      window.dispatchEvent(new CustomEvent('positions:tickerClicked', { 
        detail: { ticker: leg.Symbol } 
      }));
      
      const handleChartTabReady = () => {
        const attemptScroll = (attempts = 0) => {
          const chartSection = document.getElementById('mobile-chart-section');
          if (chartSection) {
            const rect = chartSection.getBoundingClientRect();
            const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
            const targetY = rect.top + scrollTop - 10;
            window.scrollTo({
              top: Math.max(0, targetY),
              behavior: 'smooth'
            });
            window.removeEventListener('positions:chartTabReady', handleChartTabReady);
          } else if (attempts < 20) {
            setTimeout(() => attemptScroll(attempts + 1), 50);
          } else {
            window.removeEventListener('positions:chartTabReady', handleChartTabReady);
          }
        };
        attemptScroll();
      };
      
      window.addEventListener('positions:chartTabReady', handleChartTabReady, { once: true });
      
      setTimeout(() => {
        const chartSection = document.getElementById('mobile-chart-section');
        if (chartSection) {
          const rect = chartSection.getBoundingClientRect();
          const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
          const targetY = rect.top + scrollTop - 10;
          window.scrollTo({
            top: Math.max(0, targetY),
            behavior: 'smooth'
          });
        }
      }, 500);
    }
  }, [canSelectTicker, leg.Symbol, activeChartId, updateChartSettings, isMobile, onDashboard, onNavigateToTrade]);

  return (
    <tr>
      <td style={{ textAlign: 'center', color: colors.text, verticalAlign: 'middle' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
          {isCancelable(order.Status) && (
            <button
              className="btn btn-sm"
              onClick={() => handleEditOrder(order)}
              title="Edit order"
              style={{ padding: '', color: colors.primary, background: 'transparent', border: 'none', outline: 'none', boxShadow: 'none', cursor: 'pointer' }}
              onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.7'; }}
              onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
              aria-label="Edit order"
            >
              <Pencil size={14} />
            </button>
          )}
        </div>
      </td>
      <td style={{ color: colors.text }}>
        <div>
          {canSelectTicker ? (
            <button
              type="button"
              onClick={handleSymbolClick}
              title={onDashboard ? 'Open on Trade' : 'Open in active chart'}
              style={{
                background: 'transparent',
                border: 'none',
                padding: 0,
                margin: 0,
                color: colors.primary,
                fontSize: '0.8rem',
                fontWeight: 700,
                cursor: 'pointer'
              }}
              onMouseEnter={(e) => { e.currentTarget.style.textDecoration = 'underline'; }}
              onMouseLeave={(e) => { e.currentTarget.style.textDecoration = 'none'; }}
            >
              {leg.Symbol}
            </button>
          ) : (
            <strong style={{ fontSize: '0.8rem', color: colors.text }}>{leg.Symbol}</strong>
          )}
        </div>
      </td>
      <td style={{ color: colors.text }}>
        <div>
          <strong style={{ fontSize: '0.8rem', color: colors.text }}>{leg.BuyOrSell}</strong>
        </div>
      </td>
      <td style={{ color: colors.text }}>
        <div>
          <strong style={{ fontSize: '0.8rem', color: colors.text }}>{leg.QuantityOrdered}</strong>
        </div>
      </td>
      <td style={{ color: colors.text }}>
        <div>
          <strong style={{ fontSize: '0.8rem', color: colors.text }}>{leg.ExecQuantity && parseFloat(leg.ExecQuantity) !== 0 ? leg.ExecQuantity : '-'}</strong>
        </div>
      </td>
      <td style={{ color: colors.text }}>
        <div>
          <strong style={{ fontSize: '0.8rem', color: colors.text }}>{order.OrderType}</strong>
        </div>
      </td>
      <td style={{ color: colors.text }}>
        <div style={{ textAlign: 'left' }}>
          {getOrderStatusBadge(order.Status)}
        </div>
      </td>
      <td style={{ color: colors.text }}>
        <div style={{ textAlign: 'left' }}>
          <strong style={{ fontSize: '0.8rem', color: colors.text }}>{order.FilledPrice ? formatCurrency(order.FilledPrice) : '-'}</strong>
        </div>
      </td>
      <td style={{ color: colors.text }}>
        <div>
          <strong style={{ fontSize: '0.8rem', color: colors.text }}>{order.Duration || '-'}</strong>
        </div>
      </td>
      <td style={{ color: colors.text }}>
        <div>
          <strong style={{ fontSize: '0.8rem', color: colors.text }}>{order.LimitPrice ? formatCurrency(order.LimitPrice) : '-'}</strong>
        </div>
      </td>
      <td style={{ color: colors.text }}>
        <div>
          <strong style={{ fontSize: '0.8rem', color: colors.text }}>{order.StopPrice ? formatCurrency(order.StopPrice) : '-'}</strong>
        </div>
      </td>
      <td style={{ color: colors.text, whiteSpace: 'nowrap' }}>
        <div>
          <strong style={{ fontSize: '0.8rem', color: colors.text }}>{formatDateTime(order.OpenedDateTime)}</strong>
        </div>
      </td>
      <td style={{ color: colors.text, whiteSpace: 'nowrap' }}>
        <div>
          <strong style={{ fontSize: '0.8rem', color: colors.text }}>{order.ClosedDateTime ? formatDateTime(order.ClosedDateTime) : '-'}</strong>
        </div>
      </td>
      <td style={{ color: colors.text }}>
        <div>
          <strong style={{ fontSize: '0.8rem', color: colors.text }}>{formatCurrency(order.CommissionFee)}</strong>
        </div>
      </td>
      <td style={{ color: colors.text }}>
        <div>
          <small style={{ fontSize: '0.75rem', opacity: 0.9, color: colors.text }}>{order.OrderID}</small>
        </div>
      </td>
      <td style={{ color: colors.text }}>
        <div>
          <small style={{ fontSize: '0.75rem', opacity: 0.9, color: colors.text }}>{order.AccountID || '-'}</small>
        </div>
      </td>
    </tr>
  );
});

OrdersTableRow.displayName = 'OrdersTableRow';

const OrdersTable = ({ renderAsChild = false, embedded = false, paginated = true }) => {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const location = useLocation();
  const { colors } = useTheme();
  const { user } = useContext(AuthContext);
  const { orders, isPaperTrading, selectedAccount } = useContext(TradingContext);
  const { activeChartId, updateChartSettings, chartLayout } = useContext(AppStateContext);
  const canSelectTicker = canAccessTradeWorkspace(user, selectedAccount) !== false;
  const onDashboard = isDashboardPath(location.pathname);
  const navigateOrderSymbolToTrade = useCallback(
    (symbol) => {
      navigateToTradeWithSymbol(navigate, symbol, user, selectedAccount);
    },
    [navigate, selectedAccount, user]
  );
  const { showMessage } = useContext(BannerContext);
  const {
    isLoadingHistorical,
    historicalOrdersLoaded,
    hasMoreHistorical,
    isFetchingMoreHistorical,
    fetchHistoricalOrders,
  } = useOrdersManager();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [sortField, setSortField] = useState('ClosedDateTime');
  const [sortDirection, setSortDirection] = useState('desc');
  // Filter states (search matches symbol or order ID)
  const [filterSymbol, setFilterSymbol] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterFilled, setFilterFilled] = useState(false);
  const [filterToday, setFilterToday] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const advanceAfterLoadRef = useRef(false);

  // Get unique statuses for filter options
  const uniqueStatuses = useMemo(() => {
    if (!orders || orders.length === 0) return [];
    const statuses = orders
      .map(order => order.Status)
      .filter(status => status)
      .filter((status, index, arr) => arr.indexOf(status) === index)
      .sort();
    return statuses;
  }, [orders]);
  
  // Column widths (resizable)
  const defaultColumnWidths = {
    Action: 32,
    Symbol: 60,
    Account: 100,
    Side: 50,
    Type: 80,
    Status: 90,
    AvgFill: 80,
    TIF: 50,
    Qty: 60,
    Filled: 60,
    Limit: 80,
    Stop: 80,
    Submitted: 140,
    FilledCanceled: 140,
    Commission: 90,
    OrderID: 140
  };
  const [columnWidths, setColumnWidths] = useState(() => {
    try {
      const raw = localStorage.getItem('ordersColumnWidths');
      const parsed = raw ? JSON.parse(raw) : null;
      return { ...defaultColumnWidths, ...(parsed || {}) };
    } catch (_) {
      return defaultColumnWidths;
    }
  });
  const [resizingCol, setResizingCol] = useState(null);
  const [resizeStartX, setResizeStartX] = useState(0);
  const [resizeStartWidth, setResizeStartWidth] = useState(0);
  const RESIZE_HITBOX_PX = 12;
  const [resizeHoverKey, setResizeHoverKey] = useState(null);
  
  // Debounce localStorage writes for column resize
  const columnWidthsDebounceRef = useRef(null);
  const pendingWidthsRef = useRef(columnWidths);
  
  React.useEffect(() => {
    pendingWidthsRef.current = columnWidths;
    // Clear existing timeout
    if (columnWidthsDebounceRef.current) {
      clearTimeout(columnWidthsDebounceRef.current);
    }
    // Debounce localStorage write
    columnWidthsDebounceRef.current = setTimeout(() => {
      try { 
        localStorage.setItem('ordersColumnWidths', JSON.stringify(pendingWidthsRef.current)); 
      } catch (_) {}
      columnWidthsDebounceRef.current = null;
    }, 300); // 300ms debounce
    
    return () => {
      if (columnWidthsDebounceRef.current) {
        clearTimeout(columnWidthsDebounceRef.current);
      }
    };
  }, [columnWidths]);

  const handleResizeStart = useCallback((key, event) => {
    event.stopPropagation();
    event.preventDefault();
    setResizingCol(key);
    setResizeStartX(event.clientX);
    setResizeStartWidth(columnWidths[key] || defaultColumnWidths[key] || 100);
  }, [columnWidths]);

  // Optimized header mouse move handler factory
  const createHeaderMouseMoveHandler = useCallback((key) => {
    return (e) => {
      const width = e.currentTarget?.clientWidth || 1;
      const offsetX = e.nativeEvent.offsetX;
      const near = width - offsetX <= RESIZE_HITBOX_PX;
      if (near) {
        setResizeHoverKey(key);
      } else if (resizeHoverKey === key) {
        setResizeHoverKey(null);
      }
    };
  }, [resizeHoverKey]);

  // Optimized header mouse leave handler factory
  const createHeaderMouseLeaveHandler = useCallback((key) => {
    return () => {
      if (resizeHoverKey === key) {
        setResizeHoverKey(null);
      }
    };
  }, [resizeHoverKey]);

  React.useEffect(() => {
    if (!resizingCol) return;
    const onMouseMove = (e) => {
      const delta = e.clientX - resizeStartX;
      const nextWidth = Math.max(60, resizeStartWidth + delta);
      setColumnWidths((prev) => ({ ...prev, [resizingCol]: nextWidth }));
    };
    const onMouseUp = () => setResizingCol(null);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, [resizingCol, resizeStartX, resizeStartWidth]);
  const { cancelOrder } = useCancelOrder();
  const { updateOrder } = useUpdateOrder();
  const [canceling, setCanceling] = useState({});
  const [orderToEdit, setOrderToEdit] = useState(null);
  const [showOrderEditModal, setShowOrderEditModal] = useState(false);

  // OPTIMIZED: Use cached formatter
  const formatCurrency = useCallback((value) => {
    if (!value || parseFloat(value) === 0) return '-';
    const num = parseFloat(value);
    return currencyFormatter.format(num);
  }, []);

  // OPTIMIZED: Memoize date formatter
  const formatDateTime = useCallback((dateTimeStr) => {
    if (!dateTimeStr) return '';
    const date = new Date(dateTimeStr);
    return date.toLocaleString();
  }, []);

  const getOrderStatusBadge = (status) => {
    let style = {};
    const upperStatus = status?.toUpperCase();

    // Completed/Success states - GREEN
    if (['FLL', 'FILLED', 'DONE'].includes(upperStatus)) {
      style = { backgroundColor: colors.success, color: 'white' }; // Green for filled orders
    }
    // Cancelled/Rejected states - RED  
    else if (['CXL', 'CANCELLED', 'REJECTED', 'REJ', 'EXPIRED', 'EXP', 'OUT CAN'].includes(upperStatus)) {
      style = { backgroundColor: colors.danger, color: 'white' }; // Red for cancelled/rejected
    }
    // Active/Working states - YELLOW/ORANGE
    else if (['ACK', 'DON', 'OPN', 'WORKING', 'PENDING', 'OPEN'].includes(upperStatus)) {
      style = { backgroundColor: colors.warning, color: 'black' }; // Orange for active/working
    }
    // Partially filled states - PURPLE
    else if (['PARTIAL', 'PARTIALLY_FILLED', 'PFD'].includes(upperStatus)) {
      style = { backgroundColor: '#9147ff', color: 'white' }; // Purple for partial fills
    }
    // Error states - BRIGHT RED
    else if (['ERROR', 'FAILED', 'TIMEOUT'].includes(upperStatus)) {
      style = { backgroundColor: '#ff4444', color: 'white' }; // Bright red for errors
    }
    // Outgoing/Pending submission - BLUE
    else if (upperStatus?.includes('OUT') && !upperStatus?.includes('CAN')) {
      style = { backgroundColor: colors.info, color: 'white' }; // Blue for outgoing orders
    }
    // Unknown/Other states - GRAY
    else {
      style = { backgroundColor: colors.bgSecondary, color: 'white' }; // Gray for unknown
    }

    return (
      <span
        style={{
          ...style,
          padding: '0.125rem 0.375rem',
          borderRadius: '0.25rem',
          fontSize: '0.65rem',
          fontWeight: '500',
          display: 'inline-block',
          textAlign: 'center',
          whiteSpace: 'nowrap'
        }}
      >
        {status}
      </span>
    );
  };

  const isCancelable = (status) => {
    const s = (status || '').toUpperCase();
    // Allow canceling active/working states, including DON
    // Exclude already canceled (CAN, OUT), filled (FLL), rejected (REJ), expired (EXP)
    const isActive = s.includes('ACK') || s === 'DON' || s.includes('OPN') || s === 'PLA' || s === 'WORKING';
    const isTerminal = s === 'CAN' || s === 'OUT' || s === 'FLL' || s === 'REJ' || s === 'EXP' || s === 'FLP';
    return isActive && !isTerminal;
  };

  // Handler for opening order edit modal
  const handleEditOrder = (order) => {
    if (!order) {
      console.error('[OrdersTable] handleEditOrder called with null order');
      return;
    }
    console.log('[OrdersTable] Opening edit modal for order:', order.OrderID, order);
    setOrderToEdit(order);
    setShowOrderEditModal(true);
  };

  // Handler for updating order price from edit modal
  const handleUpdateOrderPrice = async (orderId, updatePayload) => {
    try {
      const order = orderToEdit || orders.find((o) => String(o.OrderID) === String(orderId));
      const result = await updateOrder(
        isPaperTrading,
        orderId,
        updatePayload,
        order?.AccountID || selectedAccount?.AccountID
      );
      if (result && result.Error) {
        throw new Error(result.Message || 'Order update failed');
      }
      showMessage(`Order ${orderId} updated successfully`, 'success');
      setShowOrderEditModal(false);
      setOrderToEdit(null);
    } catch (error) {
      showMessage(error.message || 'Order update failed', 'danger');
      throw error;
    }
  };

  // Handler for canceling order from edit modal
  const handleCancelOrderFromEdit = async (orderId) => {
    try {
      const order = orderToEdit || orders.find((o) => String(o.OrderID) === String(orderId));
      const result = await cancelOrder(
        isPaperTrading,
        orderId,
        order?.AccountID || selectedAccount?.AccountID
      );
      const failure = parseCancelOrderFailure(result);
      if (failure) {
        showMessage(`Failed to cancel order: ${failure}`, 'danger');
        throw new Error(failure);
      }
      showMessage(`Order ${orderId} cancelled successfully`, 'success');
      setShowOrderEditModal(false);
      setOrderToEdit(null);
    } catch (error) {
      showMessage(`Error cancelling order: ${error.message || 'Unknown error'}`, 'danger');
      throw error;
    }
  };

  // Handler for closing order edit modal
  const handleCloseOrderEditModal = () => {
    setShowOrderEditModal(false);
    setOrderToEdit(null);
  };

  const handleCancel = async (order) => {
    const orderId = order?.OrderID;
    if (!orderId) return;
    setCanceling((prev) => ({ ...prev, [orderId]: true }));
    try {
      const result = await cancelOrder(
        isPaperTrading,
        orderId,
        order?.AccountID || selectedAccount?.AccountID
      );
      const failure = parseCancelOrderFailure(result);
      if (failure) {
        showMessage(`Cancel failed: ${failure}`, 'danger', true, 'banner');
        return;
      }
      showMessage(`Canceled order ${orderId}`, 'success', true, 'banner');
    } catch (err) {
      showMessage(`Cancel failed: ${err?.message || 'Unknown error'}`, 'danger', true, 'banner');
    } finally {
      setCanceling((prev) => ({ ...prev, [orderId]: false }));
    }
  };

  const handleSort = (field) => {
    if (resizingCol) return;
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? <SortUp /> : <SortDown />;
  };

  const isSameLocalDay = useCallback((dateLike) => {
    if (dateLike == null) return false;
    const ms = typeof dateLike === 'number' ? dateLike : new Date(dateLike).getTime();
    if (!Number.isFinite(ms) || ms <= 0) return false;
    return new Date(ms).toDateString() === new Date().toDateString();
  }, []);

  const sortedOrders = useMemo(() => {
    if (!orders || orders.length === 0) return [];
    
    // Apply filters first
    let filteredOrders = orders.filter(order => {
      const symbol = order.Legs?.[0]?.Symbol || '';
      const status = order.Status || '';
      const filledQty = parseFloat(order.Legs?.[0]?.ExecQuantity) || 0;
      const orderedQty = parseFloat(order.Legs?.[0]?.QuantityOrdered) || 0;
      const isFilled = filledQty > 0 && filledQty >= orderedQty;
      
      const q = filterSymbol.trim().toLowerCase();
      const orderIdStr = String(order.OrderID ?? '');
      const searchMatch =
        !q ||
        symbol.toLowerCase().includes(q) ||
        orderIdStr.toLowerCase().includes(q);
      const statusMatch = !filterStatus || status === filterStatus;
      const filledMatch = !filterFilled || isFilled;
      const todayMatch =
        !filterToday ||
        isSameLocalDay(order.OpenedDateTime) ||
        isSameLocalDay(getOrderFillTimeMs(order)) ||
        isSameLocalDay(order.ClosedDateTime);
      
      return searchMatch && statusMatch && filledMatch && todayMatch;
    });
    
    return [...filteredOrders].sort((a, b) => {
      if (filterFilled) {
        const aFill = getOrderFillTimeMs(a) ?? 0;
        const bFill = getOrderFillTimeMs(b) ?? 0;
        return bFill - aFill;
      }

      let aValue, bValue;
      
      switch (sortField) {
        case 'Action':
          aValue = isCancelable(a.Status) ? 1 : 0;
          bValue = isCancelable(b.Status) ? 1 : 0;
          break;
        case 'OpenedDateTime':
          aValue = new Date(a.OpenedDateTime || 0).getTime();
          bValue = new Date(b.OpenedDateTime || 0).getTime();
          break;
        case 'ClosedDateTime':
          aValue = getOrderFillTimeMs(a) ?? 0;
          bValue = getOrderFillTimeMs(b) ?? 0;
          break;
        case 'Symbol':
          aValue = a.Legs?.[0]?.Symbol || '';
          bValue = b.Legs?.[0]?.Symbol || '';
          break;
        case 'Account':
          aValue = a.AccountID || '';
          bValue = b.AccountID || '';
          break;
        case 'Side':
          aValue = a.Legs?.[0]?.BuyOrSell || '';
          bValue = b.Legs?.[0]?.BuyOrSell || '';
          break;
        case 'Type':
          aValue = a.OrderType || '';
          bValue = b.OrderType || '';
          break;
        case 'Status':
          aValue = a.Status || '';
          bValue = b.Status || '';
          break;
        case 'AvgFill':
          aValue = parseFloat(a.FilledPrice) || 0;
          bValue = parseFloat(b.FilledPrice) || 0;
          break;
        case 'TIF':
          aValue = a.Duration || '';
          bValue = b.Duration || '';
          break;
        case 'Qty':
          aValue = parseFloat(a.Legs?.[0]?.QuantityOrdered) || 0;
          bValue = parseFloat(b.Legs?.[0]?.QuantityOrdered) || 0;
          break;
        case 'Filled':
          aValue = parseFloat(a.Legs?.[0]?.ExecQuantity) || 0;
          bValue = parseFloat(b.Legs?.[0]?.ExecQuantity) || 0;
          break;
        case 'LimitPrice':
        case 'StopPrice':
        case 'FilledPrice':
        case 'CommissionFee':
          aValue = parseFloat(a[sortField]) || 0;
          bValue = parseFloat(b[sortField]) || 0;
          break;
        case 'OrderID':
          aValue = a.OrderID || '';
          bValue = b.OrderID || '';
          break;
        default:
          aValue = a[sortField] || '';
          bValue = b[sortField] || '';
      }
      
      if (sortDirection === 'asc') {
        return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      } else {
        return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
      }
    });
  }, [orders, sortField, sortDirection, filterSymbol, filterStatus, filterFilled, filterToday, isSameLocalDay]);

  const totalPages = paginated
    ? Math.max(1, Math.ceil(sortedOrders.length / ORDERS_PAGE_SIZE))
    : 1;

  useEffect(() => {
    if (!paginated) return;
    setCurrentPage(1);
  }, [
    paginated,
    selectedAccount?.AccountID,
    filterSymbol,
    filterStatus,
    filterFilled,
    filterToday,
    sortField,
    sortDirection,
  ]);

  useEffect(() => {
    if (!paginated) return;
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [paginated, currentPage, totalPages]);

  const displayOrders = useMemo(() => {
    if (!paginated) return sortedOrders;
    const startIndex = (currentPage - 1) * ORDERS_PAGE_SIZE;
    return sortedOrders.slice(startIndex, startIndex + ORDERS_PAGE_SIZE);
  }, [sortedOrders, paginated, currentPage]);

  const paginationRangeStart = paginated && sortedOrders.length > 0
    ? (currentPage - 1) * ORDERS_PAGE_SIZE + 1
    : 0;
  const paginationRangeEnd = paginated
    ? Math.min(currentPage * ORDERS_PAGE_SIZE, sortedOrders.length)
    : sortedOrders.length;

  const embeddedSurface = embedded && renderAsChild;

  const handleNextPage = useCallback(() => {
    if (currentPage < totalPages) {
      setCurrentPage((prev) => Math.min(totalPages, prev + 1));
      return;
    }
    if (hasMoreHistorical && !isFetchingMoreHistorical) {
      advanceAfterLoadRef.current = true;
      fetchHistoricalOrders();
    }
  }, [
    currentPage,
    totalPages,
    hasMoreHistorical,
    isFetchingMoreHistorical,
    fetchHistoricalOrders,
  ]);

  useEffect(() => {
    if (!paginated || !advanceAfterLoadRef.current || isFetchingMoreHistorical) return;
    if (currentPage < totalPages) {
      advanceAfterLoadRef.current = false;
      setCurrentPage((prev) => prev + 1);
      return;
    }
    if (!hasMoreHistorical) advanceAfterLoadRef.current = false;
  }, [paginated, isFetchingMoreHistorical, currentPage, totalPages, hasMoreHistorical]);

  const canLoadMoreFromApi = hasMoreHistorical && currentPage >= totalPages;

  const ordersPaginationFooter = paginated && sortedOrders.length > 0 && (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 12,
        padding: '10px 16px',
        borderTop: `1px solid ${colors.borderLight}`,
        fontSize: '0.75rem',
        color: colors.textMuted,
        backgroundColor: colors.bgSecondary,
      }}
    >
      <span>
        Showing {paginationRangeStart}–{paginationRangeEnd}
      </span>
      {(totalPages > 1 || canLoadMoreFromApi) && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <Button
            variant="outline-light"
            size="sm"
            onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
            style={{
              borderColor: colors.border,
              color: colors.text,
              backgroundColor: 'transparent',
              fontSize: '0.75rem',
            }}
          >
            Previous
          </Button>
          <Button
            variant="outline-light"
            size="sm"
            onClick={handleNextPage}
            disabled={currentPage >= totalPages && (!hasMoreHistorical || isFetchingMoreHistorical)}
            style={{
              borderColor: colors.border,
              color: colors.text,
              backgroundColor: 'transparent',
              fontSize: '0.75rem',
            }}
          >
            {isFetchingMoreHistorical ? 'Loading…' : canLoadMoreFromApi ? 'Load more' : 'Next'}
          </Button>
        </div>
      )}
    </div>
  );

  const tableContent = (
    <div className="table-responsive" style={{ overflowX: 'auto' }}>
      {!orders || orders.length === 0 ? (
        <p className="text-center py-3">
          {isLoadingHistorical ? 'Loading historical orders...' : 'No orders to display.'}
        </p>
      ) : (
        <Table
          bordered={!embeddedSurface}
          hover
          size="sm"
          className={embeddedSurface ? 'orders-table orders-table--embedded' : 'orders-table'}
          style={{
            fontSize: '0.8rem',
            margin: renderAsChild ? 0 : undefined,
            backgroundColor: colors.bgPrimary,
            color: colors.text,
            '--bs-table-bg': embeddedSurface ? colors.bgSecondary : colors.bgPrimary,
            '--bs-table-hover-bg': embeddedSurface ? colors.bgHeader : colors.bgSecondary,
            '--bs-table-border-color': colors.border,
            '--bs-table-color': colors.text,
          }}
        >
                <thead
                  className={resizingCol ? 'orders-thead-resizing' : undefined}
                  style={{ color: colors.text }}
                >
          <tr>
            <th style={{ width: columnWidths.Action, minWidth: columnWidths.Action, position: 'relative', cursor: resizeHoverKey === 'Action' || resizingCol === 'Action' ? 'col-resize' : 'pointer', color: colors.text }}
                onMouseMove={createHeaderMouseMoveHandler('Action')}
                onMouseLeave={createHeaderMouseLeaveHandler('Action')}
                onClick={() => handleSort('Action')}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span></span>
                {getSortIcon('Action')}
              </div>
              <ColResizeHandle columnKey="Action" onResizeStart={handleResizeStart} />
            </th>
                    <th 
                      style={{ cursor: (resizeHoverKey === 'Symbol' || resizingCol === 'Symbol') ? 'col-resize' : 'pointer', fontSize: '0.8rem', whiteSpace: 'nowrap', position: 'relative', width: columnWidths.Symbol, minWidth: columnWidths.Symbol, color: colors.text }}
                      onMouseMove={createHeaderMouseMoveHandler('Symbol')}
                      onMouseLeave={createHeaderMouseLeaveHandler('Symbol')}
                      onClick={() => handleSort('Symbol')}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span>Symbol</span>
                        {getSortIcon('Symbol')}
                      </div>
                      <ColResizeHandle columnKey="Symbol" onResizeStart={handleResizeStart} />
                    </th>
                    <th style={{ fontSize: '0.8rem', whiteSpace: 'nowrap', position: 'relative', width: columnWidths.Side, minWidth: columnWidths.Side, cursor: (resizeHoverKey === 'Side' || resizingCol === 'Side') ? 'col-resize' : 'pointer', color: colors.text }}
                        onMouseMove={createHeaderMouseMoveHandler('Side')}
                        onMouseLeave={createHeaderMouseLeaveHandler('Side')}
                        onClick={() => handleSort('Side')}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span>Side</span>
                        {getSortIcon('Side')}
                      </div>
                      <ColResizeHandle columnKey="Side" onResizeStart={handleResizeStart} />
                    </th>
                    <th style={{ fontSize: '0.8rem', whiteSpace: 'nowrap', position: 'relative', width: columnWidths.Qty, minWidth: columnWidths.Qty, cursor: (resizeHoverKey === 'Qty' || resizingCol === 'Qty') ? 'col-resize' : 'pointer', color: colors.text }}
                        onMouseMove={createHeaderMouseMoveHandler('Qty')}
                        onMouseLeave={createHeaderMouseLeaveHandler('Qty')}
                        onClick={() => handleSort('Qty')}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span>Qty</span>
                        {getSortIcon('Qty')}
                      </div>
                      <ColResizeHandle columnKey="Qty" onResizeStart={handleResizeStart} />
                    </th>
                    <th style={{ fontSize: '0.8rem', whiteSpace: 'nowrap', position: 'relative', width: columnWidths.Filled, minWidth: columnWidths.Filled, cursor: (resizeHoverKey === 'Filled' || resizingCol === 'Filled') ? 'col-resize' : 'pointer', color: colors.text }}
                        onMouseMove={createHeaderMouseMoveHandler('Filled')}
                        onMouseLeave={createHeaderMouseLeaveHandler('Filled')}
                        onClick={() => handleSort('Filled')}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span>Filled</span>
                        {getSortIcon('Filled')}
                      </div>
                      <ColResizeHandle columnKey="Filled" onResizeStart={handleResizeStart} />
                    </th>
                    <th style={{ fontSize: '0.8rem', whiteSpace: 'nowrap', position: 'relative', width: columnWidths.Type, minWidth: columnWidths.Type, cursor: (resizeHoverKey === 'Type' || resizingCol === 'Type') ? 'col-resize' : 'pointer', color: colors.text }}
                        onMouseMove={createHeaderMouseMoveHandler('Type')}
                        onMouseLeave={createHeaderMouseLeaveHandler('Type')}
                        onClick={() => handleSort('Type')}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span>Type</span>
                        {getSortIcon('Type')}
                      </div>
                      <ColResizeHandle columnKey="Type" onResizeStart={handleResizeStart} />
                    </th>
                    <th style={{ fontSize: '0.8rem', whiteSpace: 'nowrap', textAlign: 'left', position: 'relative', width: columnWidths.Status, minWidth: columnWidths.Status, cursor: (resizeHoverKey === 'Status' || resizingCol === 'Status') ? 'col-resize' : 'pointer', color: colors.text }}
                        onMouseMove={createHeaderMouseMoveHandler('Status')}
                        onMouseLeave={createHeaderMouseLeaveHandler('Status')}
                        onClick={() => handleSort('Status')}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span>Status</span>
                        {getSortIcon('Status')}
                      </div>
                      <ColResizeHandle columnKey="Status" onResizeStart={handleResizeStart} />
                    </th>
                    <th style={{ fontSize: '0.8rem', whiteSpace: 'nowrap', textAlign: 'left', position: 'relative', width: columnWidths.AvgFill, minWidth: columnWidths.AvgFill, cursor: (resizeHoverKey === 'AvgFill' || resizingCol === 'AvgFill') ? 'col-resize' : 'pointer', color: colors.text }}
                        onMouseMove={createHeaderMouseMoveHandler('AvgFill')}
                        onMouseLeave={createHeaderMouseLeaveHandler('AvgFill')}
                        onClick={() => handleSort('AvgFill')}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span>Avg Fill</span>
                        {getSortIcon('AvgFill')}
                      </div>
                      <ColResizeHandle columnKey="AvgFill" onResizeStart={handleResizeStart} />
                    </th>
                    <th style={{ fontSize: '0.8rem', whiteSpace: 'nowrap', position: 'relative', width: columnWidths.TIF, minWidth: columnWidths.TIF, cursor: (resizeHoverKey === 'TIF' || resizingCol === 'TIF') ? 'col-resize' : 'pointer', color: colors.text }}
                        onMouseMove={createHeaderMouseMoveHandler('TIF')}
                        onMouseLeave={createHeaderMouseLeaveHandler('TIF')}
                        onClick={() => handleSort('TIF')}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span>TIF</span>
                        {getSortIcon('TIF')}
                      </div>
                      <ColResizeHandle columnKey="TIF" onResizeStart={handleResizeStart} />
                    </th>
                    <th style={{ fontSize: '0.8rem', whiteSpace: 'nowrap', position: 'relative', width: columnWidths.Limit, minWidth: columnWidths.Limit, cursor: (resizeHoverKey === 'Limit' || resizingCol === 'Limit') ? 'col-resize' : 'pointer', color: colors.text }}
                        onMouseMove={createHeaderMouseMoveHandler('Limit')}
                        onMouseLeave={createHeaderMouseLeaveHandler('Limit')}
                        onClick={() => handleSort('LimitPrice')}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span>Limit</span>
                        {getSortIcon('LimitPrice')}
                      </div>
                      <ColResizeHandle columnKey="Limit" onResizeStart={handleResizeStart} />
                    </th>
                    <th style={{ fontSize: '0.8rem', whiteSpace: 'nowrap', position: 'relative', width: columnWidths.Stop, minWidth: columnWidths.Stop, cursor: (resizeHoverKey === 'Stop' || resizingCol === 'Stop') ? 'col-resize' : 'pointer', color: colors.text }}
                        onMouseMove={createHeaderMouseMoveHandler('Stop')}
                        onMouseLeave={createHeaderMouseLeaveHandler('Stop')}
                        onClick={() => handleSort('StopPrice')}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span>Stop</span>
                        {getSortIcon('StopPrice')}
                      </div>
                      <ColResizeHandle columnKey="Stop" onResizeStart={handleResizeStart} />
                    </th>
                    <th 
                      style={{ cursor: (resizeHoverKey === 'Submitted' || resizingCol === 'Submitted') ? 'col-resize' : 'pointer', fontSize: '0.8rem', whiteSpace: 'nowrap', position: 'relative', width: columnWidths.Submitted, minWidth: columnWidths.Submitted, color: colors.text }}
                      onMouseMove={createHeaderMouseMoveHandler('Submitted')}
                      onMouseLeave={createHeaderMouseLeaveHandler('Submitted')}
                      onClick={() => handleSort('OpenedDateTime')}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span>Submitted</span>
                        {getSortIcon('OpenedDateTime')}
                      </div>
                      <ColResizeHandle columnKey="Submitted" onResizeStart={handleResizeStart} />
                    </th>
                    <th
                      style={{ cursor: (resizeHoverKey === 'FilledCanceled' || resizingCol === 'FilledCanceled') ? 'col-resize' : 'pointer', fontSize: '0.8rem', whiteSpace: 'nowrap', position: 'relative', width: columnWidths.FilledCanceled, minWidth: columnWidths.FilledCanceled, color: colors.text }}
                      onMouseMove={createHeaderMouseMoveHandler('FilledCanceled')}
                      onMouseLeave={createHeaderMouseLeaveHandler('FilledCanceled')}
                      onClick={() => handleSort('ClosedDateTime')}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span>Filled/Canceled</span>
                        {getSortIcon('ClosedDateTime')}
                      </div>
                      <ColResizeHandle columnKey="FilledCanceled" onResizeStart={handleResizeStart} />
                    </th>
                    <th style={{ fontSize: '0.8rem', whiteSpace: 'nowrap', position: 'relative', width: columnWidths.Commission, minWidth: columnWidths.Commission, cursor: (resizeHoverKey === 'Commission' || resizingCol === 'Commission') ? 'col-resize' : 'pointer', color: colors.text }}
                        onMouseMove={createHeaderMouseMoveHandler('Commission')}
                        onMouseLeave={createHeaderMouseLeaveHandler('Commission')}
                        onClick={() => handleSort('CommissionFee')}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span>Commission</span>
                        {getSortIcon('CommissionFee')}
                      </div>
                      <ColResizeHandle columnKey="Commission" onResizeStart={handleResizeStart} />
                    </th>
            <th style={{ fontSize: '0.8rem', whiteSpace: 'nowrap', position: 'relative', width: columnWidths.OrderID, minWidth: columnWidths.OrderID, cursor: (resizeHoverKey === 'OrderID' || resizingCol === 'OrderID') ? 'col-resize' : 'pointer', color: colors.text }}
                onMouseMove={createHeaderMouseMoveHandler('OrderID')}
                onMouseLeave={createHeaderMouseLeaveHandler('OrderID')}
                onClick={() => handleSort('OrderID')}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span>Order ID</span>
                {getSortIcon('OrderID')}
              </div>
              <ColResizeHandle columnKey="OrderID" onResizeStart={handleResizeStart} />
            </th>
            <th
              style={{ fontSize: '0.8rem', whiteSpace: 'nowrap', position: 'relative', width: columnWidths.Account, minWidth: columnWidths.Account, cursor: (resizeHoverKey === 'Account' || resizingCol === 'Account') ? 'col-resize' : 'pointer', color: colors.text }}
              onMouseMove={createHeaderMouseMoveHandler('Account')}
              onMouseLeave={createHeaderMouseLeaveHandler('Account')}
              onClick={() => handleSort('Account')}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span>Account</span>
                {getSortIcon('Account')}
              </div>
              <ColResizeHandle columnKey="Account" onResizeStart={handleResizeStart} />
            </th>
                  </tr>
                </thead>
                <tbody>
                  {displayOrders.map((order) => (
                    <OrdersTableRow
                      key={order.OrderID}
                      order={order}
                      colors={colors}
                      formatCurrency={formatCurrency}
                      formatDateTime={formatDateTime}
                      getOrderStatusBadge={getOrderStatusBadge}
                      isCancelable={isCancelable}
                      handleEditOrder={handleEditOrder}
                      activeChartId={activeChartId}
                      updateChartSettings={updateChartSettings}
                      isMobile={isMobile}
                      canSelectTicker={canSelectTicker}
                      onDashboard={onDashboard}
                      onNavigateToTrade={navigateOrderSymbolToTrade}
                    />
                  ))}
                </tbody>
              </Table>
      )}
    </div>
  );

  if (renderAsChild) {
    return (
      <div>
        <style>
          {`
            .orders-search-input::placeholder {
              color: #a0a0a0 !important;
              opacity: 0.7;
            }
          `}
        </style>
        {/* Filter Controls */}
        <div style={{ 
          padding: isMobile ? '12px 8px' : '12px 16px', 
          borderBottom: `1px solid ${colors.borderLight}`,
          backgroundColor: colors.bgSecondary,
          display: 'flex',
          alignItems: 'center',
          gap: isMobile ? '6px' : '16px',
          flexWrap: isMobile ? 'nowrap' : 'wrap'
        }}>
          {/* Symbol / order ID search */}
          <div style={{ minWidth: isMobile ? '80px' : '150px', width: isMobile ? 'auto' : '150px', flex: isMobile ? '1' : 'none' }}>
            <Form.Control
              type="text"
              placeholder={isMobile ? "Sym / ID..." : "Symbol or order ID..."}
              aria-label="Search orders by symbol or order ID"
              size="sm"
              value={filterSymbol}
              onChange={(e) => setFilterSymbol(e.target.value)}
              className="orders-search-input"
              style={{
                backgroundColor: colors.bgInput,
                borderColor: colors.primary,
                color: colors.text,
                fontSize: '0.8rem'
              }}
            />
          </div>
          
          {/* Status Dropdown */}
          <div style={{ minWidth: isMobile ? '90px' : '150px', flex: isMobile ? '1.2' : 'none' }}>
            <Form.Select
              size="sm"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              style={{
                backgroundColor: colors.bgInput,
                borderColor: colors.borderLight,
                color: colors.text,
                fontSize: '0.8rem'
              }}
            >
              <option value="">All Statuses</option>
              <optgroup label="Filled">
                <option value="FLL">FLL - Filled</option>
                <option value="FLP">FLP - Partial Fill (UROut)</option>
              </optgroup>
              <optgroup label="Open/Active">
                <option value="ACK">ACK - Received</option>
                <option value="DON">DON - Queued</option>
                <option value="OPN">OPN - Sent</option>
                <option value="PLA">PLA - Sending</option>
                <option value="FPR">FPR - Partial Fill (Alive)</option>
                <option value="SUS">SUS - Suspended</option>
              </optgroup>
              <optgroup label="Canceled">
                <option value="CAN">CAN - Canceled</option>
                <option value="EXP">EXP - Expired</option>
                <option value="OUT">OUT - UROut</option>
                <option value="TSC">TSC - Trade Server Canceled</option>
              </optgroup>
              <optgroup label="Rejected">
                <option value="REJ">REJ - Rejected</option>
              </optgroup>
            </Form.Select>
          </div>
          
          {/* Filled Checkbox */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0, whiteSpace: 'nowrap' }}>
            <Form.Check
              type="checkbox"
              id="filled-filter"
              checked={filterFilled}
              onChange={(e) => setFilterFilled(e.target.checked)}
              style={{ margin: 0 }}
            />
            <Form.Label 
              htmlFor="filled-filter" 
              style={{ 
                fontSize: isMobile ? '0.75rem' : '0.8rem', 
                color: colors.text,
                margin: 0,
                cursor: 'pointer'
              }}
            >
              Filled
            </Form.Label>
          </div>

          {/* Today Checkbox */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0, whiteSpace: 'nowrap' }}>
            <Form.Check
              type="checkbox"
              id="today-filter"
              checked={filterToday}
              onChange={(e) => setFilterToday(e.target.checked)}
              style={{ margin: 0 }}
            />
            <Form.Label
              htmlFor="today-filter"
              style={{
                fontSize: isMobile ? '0.75rem' : '0.8rem',
                color: colors.text,
                margin: 0,
                cursor: 'pointer'
              }}
            >
              Today
            </Form.Label>
          </div>
          
          {/* Clear Filters Button */}
          {(filterSymbol || filterStatus || filterFilled || filterToday) && (
            <button
              type="button"
              className="btn btn-link btn-sm"
              onClick={() => {
                setFilterSymbol('');
                setFilterStatus('');
                setFilterFilled(false);
                setFilterToday(false);
              }}
              style={{ 
                color: colors.primary,
                textDecoration: 'none',
                fontSize: '0.75rem',
                padding: '4px 8px'
              }}
            >
              Clear
            </button>
          )}
        </div>
        
        {/* Filter Summary */}
        {(filterSymbol || filterStatus || filterFilled || filterToday) && (
          <div style={{ 
            padding: '8px 16px', 
            backgroundColor: colors.bgSecondary,
            borderBottom: `1px solid ${colors.borderLight}`,
            fontSize: '0.75rem',
            color: colors.textMuted
          }}>
            Showing {sortedOrders.length} of {orders?.length || 0} orders
          </div>
        )}
        {tableContent}
        {ordersPaginationFooter}
        
        {/* Order Edit Modal */}
        <OrderEditModal
          show={showOrderEditModal}
          onHide={handleCloseOrderEditModal}
          onUpdatePrice={handleUpdateOrderPrice}
          onCancelOrder={handleCancelOrderFromEdit}
          order={orderToEdit}
          ticker={orderToEdit?.Legs?.[0]?.Symbol || orderToEdit?.Symbol || ''}
        />
      </div>
    );
  }

  return (
    <Card className="mb-3" bg="dark" text="white">
      <Card.Header 
        className="d-flex justify-content-between align-items-center"
        style={{ cursor: 'pointer' }}
        onClick={() => setIsCollapsed(!isCollapsed)}
      >
        <h5 className="mb-0">Orders</h5>
        <div>
          {isCollapsed ? <ChevronRight /> : <ChevronDown />}
        </div>
      </Card.Header>
      
      {!isCollapsed && (
        <>
          <style>
            {`
              .orders-search-input-card::placeholder {
                color: #a0a0a0 !important;
                opacity: 0.7;
              }
            `}
          </style>
          {/* Filter Controls */}
          <div style={{ 
            padding: isMobile ? '12px 8px' : '12px 16px', 
            borderBottom: `1px solid ${colors.borderLight}`,
            backgroundColor: colors.bgSecondary,
            display: 'flex',
            alignItems: 'center',
            gap: isMobile ? '6px' : '16px',
            flexWrap: isMobile ? 'nowrap' : 'wrap'
          }}>
            {/* Symbol / order ID search */}
            <div style={{ minWidth: isMobile ? '80px' : '150px', width: isMobile ? 'auto' : '150px', flex: isMobile ? '1' : 'none' }}>
              <Form.Control
                type="text"
                placeholder={isMobile ? "Sym / ID..." : "Symbol or order ID..."}
                aria-label="Search orders by symbol or order ID"
                size="sm"
                value={filterSymbol}
                onChange={(e) => setFilterSymbol(e.target.value)}
                className="orders-search-input-card"
                style={{
                  backgroundColor: colors.bgInput,
                  borderColor: colors.primary,
                  color: colors.text,
                  fontSize: '0.8rem'
                }}
              />
            </div>
            
            {/* Status Dropdown */}
            <div style={{ minWidth: isMobile ? '90px' : '150px', flex: isMobile ? '1.2' : 'none' }}>
              <Form.Select
                size="sm"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                style={{
                  backgroundColor: colors.bgInput,
                  borderColor: colors.borderLight,
                  color: colors.text,
                  fontSize: '0.8rem'
                }}
              >
                <option value="">All Statuses</option>
                <optgroup label="Filled">
                  <option value="FLL">FLL - Filled</option>
                  <option value="FLP">FLP - Partial Fill (UROut)</option>
                </optgroup>
                <optgroup label="Open/Active">
                  <option value="ACK">ACK - Received</option>
                  <option value="DON">DON - Queued</option>
                  <option value="OPN">OPN - Sent</option>
                  <option value="PLA">PLA - Sending</option>
                  <option value="FPR">FPR - Partial Fill (Alive)</option>
                  <option value="SUS">SUS - Suspended</option>
                </optgroup>
                <optgroup label="Canceled">
                  <option value="CAN">CAN - Canceled</option>
                  <option value="EXP">EXP - Expired</option>
                  <option value="OUT">OUT - UROut</option>
                  <option value="TSC">TSC - Trade Server Canceled</option>
                </optgroup>
                <optgroup label="Rejected">
                  <option value="REJ">REJ - Rejected</option>
                </optgroup>
              </Form.Select>
            </div>
            
            {/* Filled Checkbox */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0, whiteSpace: 'nowrap' }}>
              <Form.Check
                type="checkbox"
                id="filled-filter-standalone"
                checked={filterFilled}
                onChange={(e) => setFilterFilled(e.target.checked)}
                style={{ margin: 0 }}
              />
              <Form.Label 
                htmlFor="filled-filter-standalone" 
                style={{ 
                  fontSize: isMobile ? '0.75rem' : '0.8rem', 
                  color: colors.text,
                  margin: 0,
                  cursor: 'pointer'
                }}
              >
                Filled
              </Form.Label>
            </div>

            {/* Today Checkbox */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0, whiteSpace: 'nowrap' }}>
              <Form.Check
                type="checkbox"
                id="today-filter-standalone"
                checked={filterToday}
                onChange={(e) => setFilterToday(e.target.checked)}
                style={{ margin: 0 }}
              />
              <Form.Label
                htmlFor="today-filter-standalone"
                style={{
                  fontSize: isMobile ? '0.75rem' : '0.8rem',
                  color: colors.text,
                  margin: 0,
                  cursor: 'pointer'
                }}
              >
                Today
              </Form.Label>
            </div>
            
            {/* Clear Filters Button */}
            {(filterSymbol || filterStatus || filterFilled || filterToday) && (
              <button
                type="button"
                className="btn btn-link btn-sm"
                onClick={() => {
                  setFilterSymbol('');
                  setFilterStatus('');
                  setFilterFilled(false);
                  setFilterToday(false);
                }}
                style={{ 
                  color: colors.primary,
                  textDecoration: 'none',
                  fontSize: '0.75rem',
                  padding: '4px 8px'
                }}
              >
                Clear
              </button>
            )}
          </div>
          
          {/* Filter Summary */}
          {(filterSymbol || filterStatus || filterFilled || filterToday) && (
            <div style={{ 
              padding: '8px 16px', 
              backgroundColor: colors.bgSecondary,
              borderBottom: `1px solid ${colors.borderLight}`,
              fontSize: '0.75rem',
              color: colors.textMuted
            }}>
              Showing {sortedOrders.length} of {orders?.length || 0} orders
            </div>
          )}
          
          <Card.Body style={{ padding: 0 }}>
            {tableContent}
            {ordersPaginationFooter}
          </Card.Body>
        </>
      )}
      
      {/* Order Edit Modal */}
      <OrderEditModal
        show={showOrderEditModal}
        onHide={handleCloseOrderEditModal}
        onUpdatePrice={handleUpdateOrderPrice}
        onCancelOrder={handleCancelOrderFromEdit}
        order={orderToEdit}
        ticker={orderToEdit?.Legs?.[0]?.Symbol || orderToEdit?.Symbol || ''}
      />
    </Card>
  );
};

// Memoize OrdersTable to prevent re-renders when unrelated context values change
export default memo(OrdersTable, (prevProps, nextProps) => {
  return (
    prevProps.renderAsChild === nextProps.renderAsChild
    && prevProps.embedded === nextProps.embedded
    && prevProps.paginated === nextProps.paginated
  );
});